import {
  bindService,
  createServiceToken,
  type AppFeatureDefinition,
} from "@nasam/kernel";
import {
  branchesRepositoryToken,
  companiesRepositoryToken,
  departmentsRepositoryToken,
  rolesRepositoryToken,
  type BranchesRepository,
  type CompaniesRepository,
  type DepartmentsRepository,
  type RolesRepository,
} from "../../contracts";
import {
  createDriversHttpRepository,
  createOperationalUsersHttpRepository,
} from "./data";
import type { DriversRepository, OperationalUsersRepository } from "./domain";
import { createUsersRoutes } from "./presentation/routes";
import { DRIVERS_VIEW, USER_MANAGEMENT_VIEW } from "./usersPermissions";

/**
 * Namespace `userManagement`, not `users`: that namespace already belongs to
 * the organization feature's account-admin screens.
 *
 * Drivers run on the real `/drivers` endpoints and operational users on the
 * real `/users` endpoints (see provideServices). Roles are real too
 * (resolved via rolesRepositoryToken).
 */
export * from "./usersPermissions";

export const driversRepositoryToken = createServiceToken<DriversRepository>(
  "users.drivers-repository",
);
export const operationalUsersRepositoryToken =
  createServiceToken<OperationalUsersRepository>(
    "users.operational-users-repository",
  );

export const usersFeature: AppFeatureDefinition = {
  id: "users",
  version: "0.1.0",
  titleKey: "userManagement:moduleTitle",
  icon: "Users",
  basePath: "/users",
  requiredPermissions: [USER_MANAGEMENT_VIEW, DRIVERS_VIEW],
  // Every CORE feature shares one "core" rail node (see mergeFeatureNavNodes);
  // this feature contributes the "User management" drawer group under it.
  navigation: [
    {
      id: "core",
      titleKey: "common:businessModules.core",
      icon: "Layers",
      placement: "rail",
      order: 10,
      children: [
        {
          id: "user-management",
          titleKey: "userManagement:permissions.userManagement",
          icon: "Users",
          placement: "drawer",
          order: 20,
          requiredPermissions: [USER_MANAGEMENT_VIEW, DRIVERS_VIEW],
          children: [
            {
              id: "users",
              titleKey: "userManagement:moduleTitle",
              icon: "UserCheck",
              placement: "drawer",
              order: 10,
              path: "/users",
              requiredPermissions: [USER_MANAGEMENT_VIEW],
            },
            {
              id: "users-drivers",
              titleKey: "userManagement:nav.drivers",
              icon: "Car",
              placement: "drawer",
              order: 20,
              path: "/users/drivers",
              requiredPermissions: [DRIVERS_VIEW],
            },
          ],
        },
      ],
    },
  ],
  routes: (context) =>
    createUsersRoutes({
      context,
      drivers: context.services.resolve(driversRepositoryToken),
      operationalUsers: context.services.resolve(operationalUsersRepositoryToken),
      // The Create Driver/User company/branch/department pickers; owned by
      // organization. The role picker + permission catalog; owned by roles.
      companies: context.services.tryResolve<CompaniesRepository>(
        companiesRepositoryToken,
      ),
      branches: context.services.tryResolve<BranchesRepository>(
        branchesRepositoryToken,
      ),
      departments: context.services.tryResolve<DepartmentsRepository>(
        departmentsRepositoryToken,
      ),
      roles: context.services.tryResolve<RolesRepository>(rolesRepositoryToken),
    }),
  permissionCatalog: [
    {
      segment: "core",
      labelKey: "userManagement:permissions.core",
      children: [
        {
          segment: "user_management",
          labelKey: "userManagement:permissions.userManagement",
          children: [
            {
              segment: "user_management_list",
              labelKey: "userManagement:permissions.users",
              actions: [
                { id: "view", labelKey: "userManagement:actions.view" },
                { id: "create", labelKey: "userManagement:actions.create" },
                { id: "update", labelKey: "userManagement:actions.update" },
                { id: "delete", labelKey: "userManagement:actions.delete" },
              ],
            },
            {
              segment: "driver_management_list",
              labelKey: "userManagement:permissions.drivers",
              actions: [
                { id: "view", labelKey: "userManagement:actions.view" },
                { id: "create", labelKey: "userManagement:actions.create" },
                { id: "update", labelKey: "userManagement:actions.update" },
                { id: "delete", labelKey: "userManagement:actions.delete" },
              ],
            },
          ],
        },
      ],
    },
  ],
  // The mock repositories stay for tests.
  provideServices: ({ httpClient }) => [
    bindService(driversRepositoryToken, createDriversHttpRepository(httpClient)),
    bindService(
      operationalUsersRepositoryToken,
      createOperationalUsersHttpRepository(httpClient),
    ),
  ],
};
